const LERP = 0.12;
const IMAGE_WIDTH = 260;
const IMAGE_HEIGHT = 340;
const ROW_SELECTOR = '[data-atelier-row]';
const IMAGE_SELECTOR = '[data-atelier-image]';

class AtelierIndex extends HTMLElement {
  /** @type {HTMLElement | null} */
  #list = null;

  /** @type {HTMLElement | null} */
  #figure = null;

  /** @type {HTMLImageElement | null} */
  #image = null;

  /** @type {MediaQueryList | null} */
  #pointerQuery = null;

  /** @type {MediaQueryList | null} */
  #motionQuery = null;

  /** @type {Element | null} */
  #activeRow = null;

  #frame = 0;
  #x = 0;
  #y = 0;
  #targetX = 0;
  #targetY = 0;
  #visible = false;
  #listening = false;
  #preloaded = false;

  connectedCallback() {
    this.#list = this.querySelector('[data-atelier-list]');

    if (!this.#list || typeof window.matchMedia !== 'function') return;

    this.#pointerQuery = window.matchMedia('(pointer: fine)');
    this.#motionQuery = window.matchMedia('(prefers-reduced-motion: no-preference)');

    this.#pointerQuery.addEventListener('change', this.#syncCapability);
    this.#motionQuery.addEventListener('change', this.#syncCapability);

    this.#syncCapability();
  }

  disconnectedCallback() {
    this.#pointerQuery?.removeEventListener('change', this.#syncCapability);
    this.#motionQuery?.removeEventListener('change', this.#syncCapability);
    this.#pointerQuery = null;
    this.#motionQuery = null;

    this.#stopListening();
    this.#stopLoop();
    this.#destroyFigure();

    this.#list = null;
    this.#activeRow = null;
    this.#preloaded = false;
  }

  #syncCapability = () => {
    const supported = Boolean(this.#pointerQuery?.matches && this.#motionQuery?.matches);

    if (supported) {
      this.#startListening();
    } else {
      this.#stopListening();
      this.#stopLoop();
      this.#destroyFigure();
      this.#visible = false;
      this.#activeRow = null;
    }
  };

  #startListening() {
    if (this.#listening || !this.#list) return;

    this.#list.addEventListener('pointerenter', this.#handlePointerEnter);
    this.#list.addEventListener('pointerover', this.#handlePointerOver);
    this.#list.addEventListener('pointermove', this.#handlePointerMove);
    this.#list.addEventListener('pointerleave', this.#handleRelease);
    window.addEventListener('blur', this.#handleRelease);
    document.addEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#listening = true;
  }

  #stopListening() {
    if (!this.#listening) return;

    this.#list?.removeEventListener('pointerenter', this.#handlePointerEnter);
    this.#list?.removeEventListener('pointerover', this.#handlePointerOver);
    this.#list?.removeEventListener('pointermove', this.#handlePointerMove);
    this.#list?.removeEventListener('pointerleave', this.#handleRelease);
    window.removeEventListener('blur', this.#handleRelease);
    document.removeEventListener('visibilitychange', this.#handleVisibilityChange);

    this.#listening = false;
  }

  /** @param {PointerEvent} event */
  #handlePointerEnter = (event) => {
    this.#track(event);
    this.#preload();
  };

  /** @param {PointerEvent} event */
  #handlePointerOver = (event) => {
    this.#track(event);

    const target = event.target;
    const row = target instanceof Element ? target.closest(ROW_SELECTOR) : null;

    // Hairlines and list padding sit between rows — keep the current state there.
    if (!row || !this.#list?.contains(row)) return;

    if (row === this.#activeRow) return;

    const source = row.getAttribute('data-atelier-image');

    if (!source) {
      this.#hide();
      return;
    }

    this.#activeRow = row;
    this.#show(source);
  };

  /** @param {PointerEvent} event */
  #handlePointerMove = (event) => {
    this.#track(event);
  };

  #handleRelease = () => {
    this.#hide();
  };

  #handleVisibilityChange = () => {
    if (document.hidden) this.#hide();
  };

  /** @param {PointerEvent} event */
  #track(event) {
    this.#targetX = event.clientX;
    this.#targetY = event.clientY;
  }

  #preload() {
    if (this.#preloaded || !this.#list) return;
    this.#preloaded = true;

    for (const row of this.#list.querySelectorAll(IMAGE_SELECTOR)) {
      const source = row.getAttribute('data-atelier-image');
      if (!source) continue;

      const preloader = new Image();
      preloader.decoding = 'async';
      preloader.src = source;
    }
  }

  /** @param {string} source */
  #show(source) {
    const figure = this.#ensureFigure();
    const image = this.#image;

    if (!figure || !image) return;

    if (image.getAttribute('src') !== source) image.setAttribute('src', source);

    if (!this.#visible) {
      this.#x = this.#targetX;
      this.#y = this.#targetY;
      this.#paint();
      figure.getBoundingClientRect();
      this.#visible = true;
      figure.classList.add('is-visible');
    }

    this.#startLoop();
  }

  #hide() {
    this.#activeRow = null;

    if (!this.#visible) return;

    this.#visible = false;
    this.#figure?.classList.remove('is-visible');
    this.#stopLoop();
  }

  #ensureFigure() {
    if (this.#figure) return this.#figure;

    const figure = document.createElement('div');
    figure.className = 'atelier-index__cursor';
    figure.setAttribute('aria-hidden', 'true');

    const image = document.createElement('img');
    image.className = 'atelier-index__cursor-image';
    image.alt = '';
    image.decoding = 'async';
    image.width = IMAGE_WIDTH;
    image.height = IMAGE_HEIGHT;

    figure.appendChild(image);
    document.body.appendChild(figure);

    this.#figure = figure;
    this.#image = image;

    return figure;
  }

  #destroyFigure() {
    this.#figure?.remove();
    this.#figure = null;
    this.#image = null;
  }

  #startLoop() {
    if (this.#frame) return;
    this.#frame = requestAnimationFrame(this.#tick);
  }

  #stopLoop() {
    if (!this.#frame) return;
    cancelAnimationFrame(this.#frame);
    this.#frame = 0;
  }

  #tick = () => {
    this.#frame = 0;

    if (!this.#visible) return;

    this.#x += (this.#targetX - this.#x) * LERP;
    this.#y += (this.#targetY - this.#y) * LERP;
    this.#paint();

    this.#frame = requestAnimationFrame(this.#tick);
  };

  #paint() {
    if (!this.#figure) return;

    const x = Math.round(this.#x - IMAGE_WIDTH / 2);
    const y = Math.round(this.#y - IMAGE_HEIGHT / 2);

    this.#figure.style.translate = `${x}px ${y}px`;
  }
}

if (!customElements.get('atelier-index')) {
  customElements.define('atelier-index', AtelierIndex);
}
